import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import ProductsCveStore from 'app/store/ProductsCveStore';
import usePageMeta from 'hooks/use-page-meta';
import useSearchParams from 'hooks/use-search-params';
import useStores from 'hooks/use-stores';
import Heading from 'main/components/Heading/Heading';
import CvssFilter from 'pages/Search/components/CvssFilter';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import RedirectLink from 'pages/Search/components/RedirectLink';
import { ROUTES } from 'pages/Search/constants';
import { noneIf, normalizeName } from 'pages/Search/utils';

import useStyles from './styles';

const Cvss = () => {
    const { classes } = useStyles();
    const history = useHistory();
    const { productsCveStore } = useStores();

    const { cvss, page } = useParams<{ cvss?: string; page?: string }>();
    const [{ pageSize }, setSearchParams] = useSearchParams();

    const layoutRef = useRef<{ scrollTop: () => void } | null>(null);

    const hasMetaPagination = Number(page || 1) > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + page : '';

    usePageMeta({
        title: `Vulnerabilities by CVSS Score ${cvss ? ` - ${cvss}` : ''}${pageMeta} | Vulners.com`,
        description: `Vulnerabilities with CVSS Score in the range of - ${cvss || '0-10'}${pageMeta}`
    });

    const setPath = useCallback(
        ({ cvss, page }) => history.push({ pathname: ROUTES.CVSS({ cvss, page }), search: history.location.search }),
        [history]
    );

    const getOptionHref = useCallback((cvss) => ROUTES.CVSS({ page: null, cvss }).concat(history.location.search), [
        history.location.search
    ]);

    const getPaginationHref = useCallback(
        (page) => ROUTES.CVSS({ page: noneIf(page, 1), cvss }).concat(history.location.search),
        [cvss, history.location.search]
    );

    useEffect(() => {
        layoutRef.current?.scrollTop();
        productsCveStore
            .setParams({
                page: Number(page || 1),
                pageSize: Number(pageSize || 100),
                cvss: cvss ? cvss.split('-').map(Number) : []
            })
            .loadItems();
    }, [cvss, page, pageSize, productsCveStore]);

    const tableData = useMemo(
        () => [
            ['Vendor Name', 'Product Name', 'Vulnerabilities'],
            ...productsCveStore.list.map(({ vendorName, productName, cveCount }, index) => [
                <RedirectLink
                    key={`${vendorName}-vulnerabilities-${index}`}
                    to={ROUTES.VENDOR({ page: null, vendorName, score: productsCveStore.score })}
                    label={normalizeName(vendorName)}
                    active={Boolean(vendorName)}
                />,
                <RedirectLink
                    key={`${vendorName}-vulnerabilities-${index}`}
                    to={ROUTES.VENDOR_PRODUCT({ page: null, vendorName, productName, score: productsCveStore.score })}
                    label={normalizeName(productName)}
                    active={Boolean(productName)}
                />,
                cveCount
            ])
        ],
        [productsCveStore.list, productsCveStore.score]
    );

    const handleScoreSelect = useCallback((cvss) => setPath({ page: null, cvss }), [setPath]);

    const handlePageChange = useCallback((page) => setPath({ page, cvss: productsCveStore.cvss.join('-') }), [
        productsCveStore,
        setPath
    ]);

    const handlePageSizeChange = useCallback(
        (pageSize) => setSearchParams({ pageSize: noneIf(pageSize, ProductsCveStore.DEFAULT_PAGE_SIZE) }),
        [setSearchParams]
    );

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <>
                    <Heading text={'Vulnerabilities – CVSS Score ' + (cvss ? ` ${cvss}` : '')} />
                    <CvssFilter
                        selected={productsCveStore.cvss.join('-') || undefined}
                        getOptionHref={getOptionHref}
                        onSelect={handleScoreSelect}
                        className={classes.filter}
                    />
                    <DataTable
                        data={tableData}
                        loading={productsCveStore.isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        page={productsCveStore.page}
                        pageSize={productsCveStore.pageSize}
                        totalCount={productsCveStore.totalCount}
                    />
                </>
            }
        />
    );
};

export default observer(Cvss);
